import BarChartIcon from '@mui/icons-material/BarChart';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import LensIcon from '@mui/icons-material/Lens';
import PhoneIcon from '@mui/icons-material/Phone';
import SettingsIcon from '@mui/icons-material/Settings';
import SosIcon from '@mui/icons-material/Sos';
import VideocamOffOutlinedIcon from '@mui/icons-material/VideocamOffOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { IconButton, Stack, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import React, { type FC, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch, useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';

import PatientLocationMenu from './PatientLocationMenu.js';
import PatientLogsMenu from './PatientLogsMenu.js';
import { usePatient } from '../../api/index.js';
import { useCheckPermissions } from '../../auth/index.js';
import useLiveLatestLog from '../../hooks/websocket-hooks/useLiveLatestLog.js';
import { toggleIsVideoPlaying } from '../../redux/reducers/common.js';
import { toggleCareInformationDrawer } from '../../redux/reducers/drawers.js';
import { isCareInformationDrawerOpen, isVideoPlayingSelector } from '../../redux/selectors.js';
import { Events, track } from '../../utils/index.js';
import CareInformationDrawer from '../Drawers/CareInformationDrawer.js';
import EmergencyButton from '../EmergencyButton.js';
import { EditLayoutToggle, LayoutTemplatePicker } from '../Layout/index.js';


interface PatientProfileBarContentProps {
  isUpdating?: boolean;
  isViewingLogs?: boolean;
}

const PatientProfileBarContent: FC<PatientProfileBarContentProps> = ({ isUpdating = false, isViewingLogs = false }) => {
  const { patientId = '' } = useParams();
  const navigate = useNavigate();
  const URLMatch = useMatch(`/patients/${patientId}`);
  const dispatch = useDispatch();
  const careInformationDrawerOpen = useSelector(isCareInformationDrawerOpen);
  const isVideoPlaying = useSelector(isVideoPlayingSelector(patientId));
  const [activeDrawerView, setActiveDrawerView] = useState<'emergencyContacts' | 'careInformation'>('careInformation');

  const { canUpdatePatient, canCreateLog, canReadLog, canReadFeed } = useCheckPermissions();

  const patient = usePatient(patientId, {
    includeCameras: true,
    includeEmergencyContacts: true,
  });
  const {
    firstName,
    lastName,
    inMonitoringHours,
    careInstructions,
    emergencyContacts,
    physicalAddress,
    timeZone,
    // active,
  } = patient?.data || {};
  const hasCameras = useMemo(() => !!(patient?.data?.cameras && patient.data.cameras.length), [patient, isUpdating, isViewingLogs]);
  const cameraTileIds = useMemo(
    () => (patient?.data?.cameras ?? []).map((c) => c.id),
    [patient?.data?.cameras],
  );
  const layoutScope = `patient:${patientId}`;
  const latestLog = useLiveLatestLog(patientId);

  return (
    <Stack direction={'row'} justifyContent="space-between" alignItems="center" spacing={2} sx={{ width: '100%' }}>
      <Stack direction={'row'} alignItems="center">
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{ mr: 2, fontWeight: 'bold', ...(!URLMatch && { cursor: 'pointer' }) }}
          onClick={() => {
            if (!URLMatch) {
              navigate(`/patients/${patientId}`);
            }
          }}
        >
          {firstName} {lastName}
        </Typography>
        <LensIcon color={inMonitoringHours ? 'success' : 'error'} />
        <Typography noWrap component="div" sx={{ marginLeft: '4px', mr: 2 }}>
          {inMonitoringHours ? 'In monitoring hour' : 'Outside monitoring hours'}
        </Typography>
        <Typography noWrap component="div" sx={{ marginLeft: '4px' }}>
          Latest Log ({latestLog?.dateTime}):
        </Typography>
        <Typography noWrap component="div" sx={{ marginLeft: '4px' }}>
          {latestLog?.description || 'No recent logs'}
        </Typography>
      </Stack>
      <Stack direction={'row'} alignItems="center">
        {hasCameras ? (
          <>
            {isUpdating || isViewingLogs ? null : canCreateLog ? (
              <PatientLogsMenu patientId={patientId} />
            ) : null}
            {isUpdating || isViewingLogs ? null : canReadFeed ? (
              <>
                <LayoutTemplatePicker scope={layoutScope} tileIds={cameraTileIds} />
                <EditLayoutToggle scope={layoutScope} />
                <Tooltip arrow color="primary" placement="bottom" title={`${isVideoPlaying ? 'Stop' : 'Play'} Video`}>
                  <IconButton
                    color="inherit"
                    aria-label="Start/Stop Video"
                    edge="start"
                    sx={{
                      mr: 2,
                      ml: 0.5,
                    }}
                    onClick={() => dispatch(toggleIsVideoPlaying(patientId))}
                  >
                    {isVideoPlaying ? <VideocamOffOutlinedIcon /> : <VideocamOutlinedIcon />}
                  </IconButton>
                </Tooltip>
              </>
            ) : null}
          </>
        ) : null}

        {isUpdating ? null : (
          <>
            <Tooltip arrow color="primary" placement="bottom" title={'Show care information'}>
              <IconButton
                color="inherit"
                aria-label="Show care information"
                edge="start"
                sx={{
                  mr: 2,
                }}
                onClick={() => {
                  dispatch(toggleCareInformationDrawer(true));
                  setActiveDrawerView('careInformation');
                  track(Events.CareInformationView, {
                    patientId,
                  });
                }}
              >
                <HelpCenterIcon />
              </IconButton>
            </Tooltip>
            <Tooltip arrow color="primary" placement="bottom" title={'Show emergency contacts'}>
              <IconButton
                color="inherit"
                aria-label="Show emergency contacts"
                edge="start"
                sx={{
                  mr: 2,
                }}
                onClick={() => {
                  dispatch(toggleCareInformationDrawer(true));
                  setActiveDrawerView('emergencyContacts');
                  track(Events.EmergencyContactsView, {
                    patientId,
                  });
                }}
              >
                <PhoneIcon />
              </IconButton>
            </Tooltip>
            <CareInformationDrawer
              activeDrawerView={activeDrawerView}
              careInstructions={careInstructions}
              emergencyContacts={emergencyContacts}
              open={careInformationDrawerOpen}
              onClose={() => dispatch(toggleCareInformationDrawer(false))}
            />
          </>
        )}
        {isUpdating ? null : <PatientLocationMenu physicalAddress={physicalAddress} timeZone={timeZone} />}
        {isUpdating ? null : canUpdatePatient ? (
          <Tooltip arrow color="primary" placement="bottom" title={'Edit patient settings'}>
            <IconButton
              color="inherit"
              aria-label="Edit patient settings"
              edge="start"
              sx={{
                mr: 2,
              }}
              onClick={() => navigate(`/patients/${patientId}/update`)}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        ) : null}
        {isViewingLogs ? null : canReadLog ? (
          <Tooltip arrow color="primary" placement="bottom" title={'View reports'}>
            <IconButton
              color="inherit"
              aria-label="View reports"
              edge="start"
              sx={{
                mr: 2,
              }}
              onClick={() => navigate(`/patients/${patientId}/logs`)}
            >
              <BarChartIcon />
            </IconButton>
          </Tooltip>
        ) : null}
        <EmergencyButton patientId={patientId}/>
      </Stack>
    </Stack>
  );
};

export default PatientProfileBarContent;
